/**
 * Seasonal lawn care tips organized by season and grass type
 *
 * Tips are practical, actionable advice based on the time of year
 * and whether the user has cool-season or warm-season grass.
 */

export type Season = "spring" | "summer" | "fall" | "winter"
export type GrassCategory = "cool-season" | "warm-season"

interface SeasonalTip {
  tip: string
  priority: "high" | "medium" | "low"
}

// Map common grass types to their category
export const grassCategoryMap: Record<string, GrassCategory> = {
  // Cool-season grasses (canonical names from GRASS_CHARACTERISTICS)
  "Kentucky Bluegrass": "cool-season",
  "Tall Fescue": "cool-season",
  "Fine Fescue": "cool-season",
  "Perennial Ryegrass": "cool-season",
  "Creeping Bentgrass": "cool-season",
  "Annual Ryegrass": "cool-season",
  "Mixed Cool-Season Blend": "cool-season",

  // Warm-season grasses (canonical names from GRASS_CHARACTERISTICS)
  "Bermudagrass": "warm-season",
  "Zoysiagrass": "warm-season",
  "St. Augustinegrass": "warm-season",
  "Centipede": "warm-season",
  "Bahiagrass": "warm-season",
  "Buffalograss": "warm-season",
  "Kikuyugrass": "warm-season",
  "Carpetgrass": "warm-season",

  // Common alternate spellings (for user input / display names)
  "Bermuda Grass": "warm-season",
  "Zoysia Grass": "warm-season",
  "St. Augustine Grass": "warm-season",
  "St. Augustine": "warm-season",
  "Centipede Grass": "warm-season",
  "Bahia Grass": "warm-season",
  "Buffalo Grass": "warm-season",
  "Carpet grass": "warm-season",
}

export function getGrassCategory(grassType: string): GrassCategory {
  return grassCategoryMap[grassType] || "cool-season"
}

// Seasonal tips by season and grass category
const seasonalTips: Record<Season, Record<GrassCategory, SeasonalTip[]>> = {
  spring: {
    "cool-season": [
      {
        tip: "Spring is prime overseeding time - soil temps are ideal at 50-65°F",
        priority: "high",
      },
      {
        tip: "Apply pre-emergent before soil hits 55°F to prevent crabgrass",
        priority: "high",
      },
      {
        tip: "Start mowing when grass reaches 3 inches - don't cut more than 1/3",
        priority: "medium",
      },
      {
        tip: "Core aerate if soil was compacted over winter",
        priority: "medium",
      },
      {
        tip: "Hold off on heavy fertilization until grass is actively growing",
        priority: "low",
      },
    ],
    "warm-season": [
      {
        tip: "Your warm-season grass is waking up - be patient with green-up",
        priority: "medium",
      },
      {
        tip: "Wait until after last frost to fertilize warm-season lawns",
        priority: "high",
      },
      {
        tip: "Hold off on heavy traffic until grass is fully out of dormancy",
        priority: "medium",
      },
      {
        tip: "Remove dead grass (thatch) once new growth appears",
        priority: "low",
      },
      {
        tip: "Pre-emergent timing is critical - apply when soil hits 55°F",
        priority: "high",
      },
    ],
  },
  summer: {
    "cool-season": [
      {
        tip: "Raise mowing height to 3-4 inches to protect roots from heat",
        priority: "high",
      },
      {
        tip: "Water deeply but infrequently - 1 inch per week total",
        priority: "high",
      },
      {
        tip: "Avoid fertilizing during heat stress - wait for cooler temps",
        priority: "medium",
      },
      {
        tip: "Your cool-season grass may go dormant in extreme heat - this is normal",
        priority: "low",
      },
      {
        tip: "Sharpen mower blades to reduce stress on heat-stressed grass",
        priority: "medium",
      },
    ],
    "warm-season": [
      {
        tip: "Your warm-season grass is thriving! This is peak growth season",
        priority: "low",
      },
      {
        tip: "Fertilize now while grass is actively growing",
        priority: "high",
      },
      {
        tip: "Maintain consistent mowing schedule - growth is rapid",
        priority: "medium",
      },
      {
        tip: "Water early morning to reduce evaporation and disease risk",
        priority: "medium",
      },
      {
        tip: "Watch for chinch bugs and other summer pests",
        priority: "medium",
      },
    ],
  },
  fall: {
    "cool-season": [
      {
        tip: "Fall is the BEST time to overseed and repair bare spots",
        priority: "high",
      },
      {
        tip: "Apply fall fertilizer to strengthen roots before winter",
        priority: "high",
      },
      {
        tip: "Continue mowing until grass stops growing",
        priority: "medium",
      },
      {
        tip: "Core aerate now for best results - soil is still warm",
        priority: "high",
      },
      {
        tip: "Clean up fallen leaves - they can smother grass",
        priority: "medium",
      },
    ],
    "warm-season": [
      {
        tip: "Growth is slowing - reduce fertilizer applications",
        priority: "medium",
      },
      {
        tip: "Lower mowing height gradually to prepare for dormancy",
        priority: "medium",
      },
      {
        tip: "Apply pre-emergent for winter weeds like poa annua",
        priority: "high",
      },
      {
        tip: "Your lawn will brown as it goes dormant - this is normal",
        priority: "low",
      },
      {
        tip: "Consider overseeding with ryegrass for winter color",
        priority: "low",
      },
    ],
  },
  winter: {
    "cool-season": [
      {
        tip: "Your grass is dormant but roots are still active",
        priority: "low",
      },
      {
        tip: "Avoid walking on frozen grass - it can damage blades",
        priority: "medium",
      },
      {
        tip: "Plan your spring lawn care strategy now",
        priority: "low",
      },
      {
        tip: "Service your mower and equipment during the off-season",
        priority: "low",
      },
      {
        tip: "Monitor for snow mold in areas with persistent snow cover",
        priority: "medium",
      },
    ],
    "warm-season": [
      {
        tip: "Your lawn is dormant - minimal care needed",
        priority: "low",
      },
      {
        tip: "Keep off the lawn when frost is present",
        priority: "medium",
      },
      {
        tip: "Water occasionally if no rain for 3+ weeks",
        priority: "low",
      },
      {
        tip: "Now is a great time to soil test for spring planning",
        priority: "medium",
      },
      {
        tip: "Watch for winter weeds and spot treat if needed",
        priority: "medium",
      },
    ],
  },
}

/**
 * Get the current season based on month
 */
export function getCurrentSeason(): Season {
  const month = new Date().getMonth()

  if (month >= 2 && month <= 4) return "spring" // Mar-May
  if (month >= 5 && month <= 7) return "summer" // Jun-Aug
  if (month >= 8 && month <= 10) return "fall"  // Sep-Nov
  return "winter" // Dec-Feb
}

/**
 * Get a random seasonal tip for the given grass type
 */
export function getSeasonalTip(
  grassType?: string,
  season?: Season
): SeasonalTip {
  const currentSeason = season || getCurrentSeason()
  const category = grassType ? getGrassCategory(grassType) : "cool-season"

  const tips = seasonalTips[currentSeason][category]

  // Prioritize high priority tips
  const highPriorityTips = tips.filter((t) => t.priority === "high")

  if (highPriorityTips.length > 0 && Math.random() > 0.3) {
    return highPriorityTips[Math.floor(Math.random() * highPriorityTips.length)]
  }

  return tips[Math.floor(Math.random() * tips.length)]
}

/**
 * Get all tips for a given season and grass category
 */
export function getAllSeasonalTips(
  grassType?: string,
  season?: Season
): SeasonalTip[] {
  const currentSeason = season || getCurrentSeason()
  const category = grassType ? getGrassCategory(grassType) : "cool-season"

  return seasonalTips[currentSeason][category]
}
